import React, { useEffect, useRef, useState } from "react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { File } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Props {
  envVariablesSet: any;
  handleEnvVariables: (variables?: any) => void;
  mode?: "paste" | "upload";
  isCreatingProject?: boolean;
}

const PLACEHOLDER_TEXT = "Paste text from an env variable file to bulk add";

const EnvVariableHandler: React.FC<Props> = ({
  envVariablesSet,
  handleEnvVariables,
  mode = "paste",
  isCreatingProject = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [placeHolderText, setPlaceHolderText] = useState(PLACEHOLDER_TEXT);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const parseEnv = (envString: string) => {
    const env: Record<string, string> = {};
    const lines = envString.split("\n");

    for (const line of lines) {
      if (!line || line.startsWith("#")) continue;

      let [key, ...valueParts] = line.split("=");
      let value = valueParts.join("=");

      key = key.trim();
      value = value.trim();

      if (value.startsWith('"') && value.endsWith('"'))
        value = value.slice(1, -1);
      if (value.startsWith("'") && value.endsWith("'"))
        value = value.slice(1, -1);

      value = value
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\\\/g, "\\")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");

      env[key] = value;
    }
    return env;
  };

  const handleEnvData = (envString: string) => {
    if (!envString) return;

    const parsedEnv = parseEnv(envString);

    const filteredEnvVariablesSet = (envVariablesSet || []).filter(
      (r) => r.key
    );
    const envVariables = Object.entries(parsedEnv).map(([key, value]) => ({
      key,
      value,
    }));

    const combined = new Map(
      filteredEnvVariablesSet.map(({ key, value }: any) => [key, value])
    );
    envVariables.forEach(({ key, value }) => combined.set(key, value));

    setPlaceHolderText("Keys added");
    setTimeout(() => setPlaceHolderText(PLACEHOLDER_TEXT), 5000);

    const result = Array.from(combined, ([key, value]) => ({ key, value }));
    handleEnvVariables(result);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const processInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnvData(inputValue);
      setInputValue("");
    }
  };

  const handlePasteEvent = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData?.getData("text") || "";
    handleEnvData(pastedText);

    setTimeout(() => {
      setInputValue("");
      adjustTextareaHeight(inputRef.current);
    }, 0);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isEnvFile = file.name.toLowerCase().endsWith(".env");

    if (!isEnvFile) {
      toast.error("Only .env files are allowed.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      handleEnvData(text);
    };
    reader.readAsText(file);

    e.target.value = "";
  };

  const adjustTextareaHeight = (element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputValue]);

  if (mode === "upload") {
    return (
      <div
        className={cn(
          "flex items-center gap-2",
          !isCreatingProject && "flex-col"
        )}
      >
        <div className={cn("relative", !isCreatingProject && "w-full")}>
          <Button
            variant="outline"
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--secondary-n-900)",
            }}
            className={cn(
              "whitespace-nowrap  rounded-md px-2 flex gap-2 text-secondary-n-50 text-xs font-medium h-8",
              isCreatingProject ? "w-min" : "w-full"
            )}
          >
            <File className="w-4 h-4" />
            <label htmlFor="file-upload" className="cursor-pointer">
              Import .env
            </label>
          </Button>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
        <div className="text-2xs text-muted-foreground">
          or paste the .env contents above
        </div>
      </div>
    );
  }

  return (
    <Textarea
      ref={inputRef}
      value={inputValue}
      placeholder={placeHolderText}
      onChange={handleInput}
      onKeyDown={processInput}
      onPaste={handlePasteEvent}
      className="min-h-[46px] transition-all duration-200"
    />
  );
};

export default React.memo(EnvVariableHandler);
